import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { KibRadio } from '@chewy/kib-choices-react'
import { formatMoney } from '@pbt/pbt-ui-components/src/utils/numberUtils'

import type { Payment as GraphQlPayment } from '~/api/graphql/generated/types'
import type { Payment } from '~/types'

import NumberField from '../../../add-payment-dialog-v2/components/NumberField'
import { useRefundDialog } from '../context/RefundDialogProvider'
import { calculateRefundableAmount } from '../utils/refundCalculations'
import { PaymentTerminalSection } from './PaymentTerminalSection'

import '@chewy/kib-choices-styles/dist/styles/kib-radio.css'
import styles from './RefundInformation.module.scss'

export const RefundInformation = () => {
  const { t } = useTranslation(['Common', 'Dialogs', 'Invoices'])
  const { refundInvoice, actions, dialogState, isLoading } = useRefundDialog()
  const [selectedPaymentId, setSelectedPaymentId] = useState<string | null>(
    null,
  )

  const payments: (GraphQlPayment | Payment)[] =
    refundInvoice?.originalInvoice?.payments || []

  // Filter payments with positive refundable amounts
  const positivePayments = payments.filter(
    (p) => (p.refundableAmount || -1) > 0,
  )

  const remainingAmount = calculateRefundableAmount(refundInvoice)
  const amountToShow = dialogState.selectedPayment
    ? Math.min(
        dialogState.selectedPayment.refundableAmount ?? 0,
        remainingAmount,
      )
    : remainingAmount

  return (
    <div className={styles.refundInformationContainer}>
      <h4 className={styles.sectionTitle}>
        {t('Dialogs:REFUND_DIALOG_V2.REFUND_INFORMATION')}
      </h4>

      {dialogState.unlinked && <PaymentTerminalSection />}

      <div className={styles.amountFieldContainer}>
        <NumberField
          isMoney
          readOnly
          label={t('Dialogs:REFUND_DIALOG_V2.AMOUNT_TO_REFUND')}
          value={amountToShow.toString()}
        />
      </div>

      {positivePayments.length > 0 && (
        <div className={styles.paymentsContainer}>
          <div className={styles.paymentsGrid}>
            {positivePayments.map((payment: GraphQlPayment | Payment) => {
              const paymentName =
                'description' in payment
                  ? payment.description
                  : payment.method?.name || ''
              const showCardDetails = payment?.method?.name === 'Credit card'

              const paymentLabel = showCardDetails
                ? `${payment?.cardType?.name} ${payment?.lastFour}`
                : paymentName

              return (
                <div className={styles.paymentItem} key={payment.id}>
                  <KibRadio
                    append={
                      <div className={styles.paymentAmount}>
                        {t('Invoices:MAX_REFUND_ALLOWED', {
                          amount: formatMoney(payment.refundableAmount ?? 0),
                        })}
                      </div>
                    }
                    disabled={isLoading}
                    label={
                      <div className={styles.paymentLabelContainer}>
                        <div className={styles.paymentName}>{paymentLabel}</div>
                      </div>
                    }
                    name="payment-selection"
                    selectedValue={selectedPaymentId}
                    value={payment.id}
                    onChange={(newValue) => {
                      const paymentId = newValue as string
                      setSelectedPaymentId(paymentId)
                      actions.setSelectedPayment(payment)
                      const paymentRefundableAmount =
                        payment.refundableAmount ?? 0
                      const calculatedRefundAmount = Math.min(
                        paymentRefundableAmount,
                        remainingAmount,
                      )
                      actions.setRefundAmount(calculatedRefundAmount)
                    }}
                  />
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
